import { Button } from '@myinterview/component-library';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ANSWER_TYPES, ASSESSMENT_QUESTIONS_TYPE, IMultipleChoiceOptionDefinition } from '../interfaces/jobInterface';
import { IMultipleAnswer, IVideoFileTextAnswer, CURRENT_QUESTION_ANSWER_TYPES } from '../interfaces/videoInterface';
import { SECONDS_LEFT_HIGHLIGHT } from '../utils/constants.utils';
import { AnswerInstructions } from './AnswerInstructions';
import { AssessmentFreeText } from './AssessmentFreeText';
import { AssessmentMultiSelect } from './AssessmentMultiSelect';
import { AssessmentSingleSelect } from './AssessmentSingleSelect';
import { CountDown } from './CountDown';
import { Question } from './Question';
import { QuestionNumber } from './ui/QuestionNumber';

interface IProps {
  currentQuestionObj: ASSESSMENT_QUESTIONS_TYPE;
  currentQuestion: number;
  numberOfQuestions: number;
  timer: number;
  onSubmitAssessment: (answer: IMultipleAnswer[] | string, isValidAnswer: boolean) => void;
}

const ANSWER_COMPONENTS = {
  [ANSWER_TYPES.TEXT]: AssessmentFreeText,
  [ANSWER_TYPES.SINGLE]: AssessmentSingleSelect,
  [ANSWER_TYPES.MULTIPLE]: AssessmentMultiSelect,
};

export const AssessmentController:React.FC<IProps> = ({ currentQuestionObj, timer, currentQuestion, numberOfQuestions, onSubmitAssessment }) => {
  const { t } = useTranslation();
  const answerToSubmit = useRef<CURRENT_QUESTION_ANSWER_TYPES>(currentQuestionObj.answerType !== ANSWER_TYPES.TEXT ? [] : '');
  const AnswerComponent = useMemo(() => ANSWER_COMPONENTS[currentQuestionObj.answerType], [currentQuestionObj.answerType]);
  const [isValidAnswer, setIsValidAnswer] = useState<boolean>(false);
  const answerOptions: IMultipleChoiceOptionDefinition[] = (currentQuestionObj.answerType !== ANSWER_TYPES.TEXT) ? currentQuestionObj.config?.answers || [] : [];

  const onSubmit = (isTimesUp: boolean) => {
    onSubmitAssessment(answerToSubmit.current, isTimesUp);
  };

  const isValidAnswerCallback = useMemo((): () => boolean => {
    switch (currentQuestionObj.answerType) {
      case ANSWER_TYPES.TEXT:
        return () => answerToSubmit.current.length >= (currentQuestionObj.config?.minChars || 1);
      case ANSWER_TYPES.SINGLE:
      case ANSWER_TYPES.MULTIPLE:
        return () => (answerToSubmit.current as IMultipleAnswer[]).some((answer) => answer.selected);
      default:
        return () => true;
    }
  }, [currentQuestionObj.answerType]);

  useEffect(() => {
    currentQuestionObj.partDuration && !timer && onSubmit(true);
  }, [timer]);

  const onAnswer = (answer: IMultipleAnswer[] | IVideoFileTextAnswer['answer']) => {
    answerToSubmit.current = answer || '';
    setIsValidAnswer(isValidAnswerCallback);
  };

  return (
    <div className="myinterview-widget-assessment myinterview-widget--rtl-support">
      <QuestionNumber currentQuestion={currentQuestion} numberOfQuestions={numberOfQuestions} />
      {!!timer && (
      <div className="myinterview-widget-assessment__countdown-wrapper">
        <CountDown countDown={timer} forceMobileCounter numberOfSecondsToHighlight={SECONDS_LEFT_HIGHLIGHT} />
      </div>
      )}
      <Question questionObj={currentQuestionObj} />
      <AnswerInstructions currentQuestionObj={currentQuestionObj} />
      <AnswerComponent
        answerOptions={answerOptions}
        onAnswer={onAnswer}
        {...(currentQuestionObj.answerType === ANSWER_TYPES.TEXT) && { maxChars: currentQuestionObj.config?.maxChars }}
      />
      <Button disabled={!isValidAnswer} className="myinterview-widget-assessment__submit-button" onClick={() => onSubmit(false)}>{t('buttons.SUBMIT')}</Button>
    </div>
  );
};
